import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography, CircularProgress, Box, Divider } from "@mui/material";
import { getListAPI as getAssetListAPI } from './AssetAPI';
import { setAssetList,setSelectedAssetIdList } from 'store/slice/ImageGallerySlice';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

const INITIAL_PAGE_SIZE = 20;
const PAGE_SIZE = 10;

const AssetPreview = ({ assetObj, selected }) => {
    const isVideo = assetObj.path.toLowerCase().endsWith('.mp4');

    return (
        <Box
            sx={{
                width: '130px',
                height: '130px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '5px',
                backgroundColor: selected ? '#e3f2fd' : '#f7f9fc',
                border: selected ? '2px solid #0050b3' : '.5px solid #b0bec5',
                overflow: 'hidden'
            }}
        >
            {isVideo ? (
                <video
                    src={assetObj.path}
                    style={{
                        width: '130px',
                        height: '130px',
                        objectFit: 'cover',
                        borderRadius: '5px',
                        background: 'transparent'
                    }}
                    muted
                    preload="metadata"
                />
            ) : (
                <img
                    src={assetObj.path}
                    alt={assetObj.name}
                    title={assetObj.name}
                    loading="lazy"
                    style={{
                        width: '130px',
                        height: '130px',
                        borderRadius: '5px',
                        background: 'transparent',
                        objectFit: 'contain'
                    }}
                />
            )}
        </Box>
    );
};

const ImageGalleryMainArea = () => {
    const dispatch = useDispatch();
    const { assetList } = useSelector((state) => state.imagegalleryslice);
    const { imageNameUnderSearch, multiSelection,selectedAssetIdList} = useSelector((state) => state.imagegalleryslice);
    const [filteredAssetList, setFilteredAssetList] = useState([]);
    const [selectedAssetListObjLocal, setSelectedAssetListObjLocal] = useState(selectedAssetIdList); // Comma separated string
    const [apiInProgress, setApiInProgress] = useState(false);
    const [viewImage, setViewImage] = useState(null);
    const [visibleCount, setVisibleCount] = useState(INITIAL_PAGE_SIZE);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const scrollContainerRef = useRef(null);

    const fetchAssetListCallBack = (data) => {
        dispatch(setAssetList(data));
        setFilteredAssetList(data);
        setApiInProgress(false);
    };
    useEffect(() => {
        dispatch(setSelectedAssetIdList(selectedAssetListObjLocal));
    }, [selectedAssetListObjLocal]);
    

    useEffect(() => {
        setApiInProgress(true);
        getAssetListAPI(fetchAssetListCallBack);
    }, []);

    useEffect(() => {
        setFilteredAssetList(
            assetList.filter(assetObj => assetObj.name.toLowerCase().includes(imageNameUnderSearch))
        );
    }, [imageNameUnderSearch, assetList]);

    useEffect(() => {
        setVisibleCount(INITIAL_PAGE_SIZE);
    }, [filteredAssetList]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;

        if (!scrollContainer || visibleCount >= filteredAssetList.length) {
            return;
        }

        if (scrollContainer.scrollHeight <= scrollContainer.clientHeight) {
            setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredAssetList.length));
        }
    }, [visibleCount, filteredAssetList.length]);

    useEffect(() => {
        if (!isLoadingMore) {
            return;
        }

        const frame = requestAnimationFrame(() => {
            setIsLoadingMore(false);
        });

        return () => cancelAnimationFrame(frame);
    }, [isLoadingMore, visibleCount]);

    const isImageSelected = (imageObj) => {
        const selectedIds = selectedAssetListObjLocal ? selectedAssetListObjLocal.split(',').filter(Boolean) : [];
        return selectedIds.includes(String(imageObj.id));
    };

    const loadMoreAssets = () => {
        if (isLoadingMore || visibleCount >= filteredAssetList.length) {
            return;
        }

        setIsLoadingMore(true);
        setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredAssetList.length));
    };

    const handleScroll = (event) => {
        const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 120;

        if (isNearBottom) {
            loadMoreAssets();
        }
    };

    return (
        <Box lg={12} sx={{ flexGrow: 1 }}>
            <Grid item ref={scrollContainerRef} sx={{ overflowY: "scroll", backgroundColor: 'white', maxHeight: '65vh' }} onScroll={handleScroll}>
                <Grid container direction="row" sx={{ width: '100%', backgroundColor: 'white', justifyContent: apiInProgress ? 'center' : 'flex-start', alignItems: 'center' }} lg={12}>
                    <>
                        {
                            apiInProgress ? <Grid item sx={{ padding: 10 }}><CircularProgress /></Grid>
                                : <>
                                    <Grid container direction="row">
                                        {filteredAssetList.slice(0, visibleCount).map(assetObj => (
                                            <Grid item lg={2} md={2} sm={3} xs={6} key={assetObj.name + assetObj.path}>
                                                <Box
                                                    sx={{
                                                        width: '150px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-start',
                                                        p: '1px 0',
                                                        m: 1,
                                                        backgroundColor: 'secondary.100',
                                                        borderRadius: '8px'
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: '150px',
                                                            height: '150px',
                                                            borderRadius: '5px',
                                                            overflow: 'hidden',
                                                            transition: 'background 0.2s',
                                                            background: isImageSelected(assetObj) ? '#e3f2fd' : 'transparent',
                                                            '&:hover': {
                                                                background: '#f0f7ff',
                                                            },
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'default',
                                                            position: 'relative',
                                                        }}
                                                        title={assetObj.name}
                                                    >
                                                        {assetObj.path.toLowerCase().endsWith('.mp4') ? (
                                                            <>
                                                                <AssetPreview
                                                                    assetObj={assetObj}
                                                                    selected={isImageSelected(assetObj)}
                                                                />
                                                                <PlayCircleOutlineIcon
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: '50%',
                                                                        left: '50%',
                                                                        transform: 'translate(-50%, -50%)',
                                                                        color: '#fff',
                                                                        fontSize: 48,
                                                                        opacity: 0.8,
                                                                        pointerEvents: 'none'
                                                                    }}
                                                                />
                                                            </>
                                                        ) : (
                                                            <AssetPreview
                                                                assetObj={assetObj}
                                                                selected={isImageSelected(assetObj)}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Typography
                                                        variant="body2"
                                                        noWrap
                                                        title={assetObj.name}
                                                        sx={{
                                                            maxWidth: '150px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            display: 'block',
                                                            cursor: 'pointer',
                                                            color: '#1976d2',
                                                            textDecoration: 'underline',
                                                            fontWeight: 500,
                                                            mt: 1,
                                                            textAlign: 'left',
                                                            '&:hover': {
                                                                color: '#1565c0',
                                                                textDecoration: 'underline',
                                                            },
                                                        }}
                                                        onClick={() => setViewImage(assetObj)}
                                                    >
                                                        {assetObj.name}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                    {isLoadingMore && visibleCount < filteredAssetList.length && (
                                        <Grid container justifyContent="center" sx={{ py: 2 }}>
                                            <CircularProgress size={20} />
                                        </Grid>
                                    )}
                                    {!isLoadingMore && visibleCount < filteredAssetList.length && (
                                        <Grid container justifyContent="center" sx={{ py: 2 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Scroll down to load more
                                            </Typography>
                                        </Grid>
                                    )}
                                    {
                                        !apiInProgress && filteredAssetList.length === 0 ? (
                                            <Grid container sx={{ justifyContent: "center", alignItems: "center", paddingTop: 5 }}>
                                                <Grid sx={{ width: '500px', backgroundColor: '#e6f7ff', borderRadius: 5 }}>
                                                    <Typography sx={{ padding: 2 }}>
                                                        <i>Click on 'Select Image' button for Image selection and then click on 'Upload' Button.</i>
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        ) : null
                                    }
                                </>
                        }
                    </>
                </Grid>
            </Grid>
            <Dialog
                open={!!viewImage}
                onClose={() => setViewImage(null)}
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        width: 'auto',
                        maxWidth: '90vw',
                        minWidth: '320px',
                        p: 0,
                        borderRadius: 2,
                    }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        pb: 0,
                        width: '100%',
                        backgroundColor: '#f0f7ff',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {viewImage?.name || "Preview"}
                    </Typography>
                    <IconButton onClick={() => setViewImage(null)}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />
                <DialogContent
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 3,
                        minWidth: '320px',
                        minHeight: '200px',
                        maxWidth: '80vw',
                        maxHeight: '70vh',
                    }}
                >
                    {viewImage?.path && viewImage.path.toLowerCase().endsWith('.mp4') ? (
                        <video
                            src={viewImage.path}
                            controls
                            style={{
                                maxWidth: '80vw',
                                maxHeight: '65vh',
                                display: 'block',
                                margin: '0 auto'
                            }}
                        />
                    ) : (
                        <img
                            src={viewImage?.path}
                            alt={viewImage?.name}
                            style={{
                                maxWidth: '80vw',
                                maxHeight: '65vh',
                                display: 'block',
                                margin: '0 auto'
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default ImageGalleryMainArea;
